import * as React from "react";
import { Box, Card, Stack, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { useNavigate } from "react-router-dom";

import { Agent, agentsKeys, useDeleteAgent } from "@/hooks/api/use-agents";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";

import { PopupAgentModal } from "./popup/popup";
import { TrashIcon } from "@phosphor-icons/react/dist/icons/Trash";

export function Agents(): React.JSX.Element {
	const {
		items: agents,
		isLoading,
		totalItems,
		refetchData: fetchAgents,
	} = useCachedTableStateWithUrl<Agent>({
		queryKey: agentsKeys.lists(),
		endpoint: "/agents",
	});

	const navigate = useNavigate();

	const {
		data: selectedAgent,
		open: isDeleteAgentModalOpen,
		handleClose: handleDeleteAgentModalClose,
		handleOpen: handleDeleteAgentModalOpen,
	} = useDialog<Agent | null>();

	const {
		data: popupAgent,
		open: isPopupAgentModalOpen,
		handleClose: handlePopupAgentModalClose,
		handleOpen: handlePopupAgentModalOpen,
	} = useDialog<Agent>();

	const deleteAgentMutation = useDeleteAgent();

	const handleDeleteAgent = async () => {
		if (selectedAgent) {
			try {
				await deleteAgentMutation.mutateAsync(selectedAgent.id);
				handleDeleteAgentModalClose();
			} catch {
				// Error handling is done in the mutation
			}
		}
	};

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "name",
					label: "Name",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "name",
				getCellValue: (row: any) => {
					return (
						<Tooltip title="View Agent" arrow>
							<Box
								tabIndex={0}
								onClick={() => navigate(`/agents/${row.id}`)}
								sx={{
									textDecoration: "none",
									color: "inherit",
									cursor: "pointer",
									width: "max-content",
									"&:hover": { textDecoration: "underline" },
								}}
							>
								{row.name}
							</Box>
						</Tooltip>
					);
				},
			},
			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Popup",
									icon: <PlusIcon width={20} height={20} weight="fill" />,
									onClick: () => handlePopupAgentModalOpen(row),
								},
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => navigate(`/agents/${row.id}`),
								},
                {
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleDeleteAgentModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, []);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Agents Management</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Configure AI agents and their conversation settings
							</Typography>
						</Box>
						<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
							<Button startIcon={<PlusIcon />} variant="contained" onClick={() => navigate("/agents/create")}>
								Create Agent
							</Button>
						</Box>
					</Stack>
					<Card>
						<CustomTable
							columns={columns}
							data={agents}
							refetchData={fetchAgents}
							isLoading={isLoading}
							totalItems={totalItems}
						/>
					</Card>
				</Stack>
			</Box>

			<DeleteModal
				open={isDeleteAgentModalOpen}
				onClose={handleDeleteAgentModalClose}
				onDelete={handleDeleteAgent}
				title="Delete Agent"
				description="Are you sure you want to delete this agent?"
			/>

			<PopupAgentModal agent={popupAgent!} open={isPopupAgentModalOpen} onClose={handlePopupAgentModalClose} />
		</React.Fragment>
	);
}
